import {
  Heart, HandCoins, Megaphone, GraduationCap, ShieldCheck, Receipt, Landmark, Copy,
} from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import DonateWidget from "@/components/DonateWidget";
import ReceiptForm from "@/components/ReceiptForm";
import { Reveal } from "@/components/Reveal";

const USES = [
  { icon: HandCoins, title: "Aides concrètes au quotidien", text: "Financer le soutien alimentaire et le soutien financier des malades, pour alléger leur quotidien et celui de leur famille." },
  { icon: Heart, title: "Aide directe aux malades", text: "Écoute, accompagnement des familles, aide aux déplacements pour les soins hebdomadaires." },
  { icon: Megaphone, title: "Sensibilisation", text: "Plaquettes, stands et campagnes pour faire connaître la maladie de Waldmann." },
  { icon: GraduationCap, title: "Information", text: "Recherche et diffusion d'informations médicales fiables, en langage clair." },
];

const BANK = {
  titulaire: "Aide au Monde et Santé",
  banque: "Crédit Agricole",
  iban: "FR76 1820 6002 2365 0593 6124 975",
  bic: "AGRIFRPP882",
};

const BankRow = ({ label, value, testid }) => {
  const copy = async () => {
    let ok = false;
    try {
      await navigator.clipboard.writeText(value);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {
        ok = false;
      }
    }
    if (ok) {
      toast.success(`${label} copié !`);
    } else {
      toast.error("Copie impossible — sélectionnez le texte manuellement.");
    }
  };
  return (
    <div className="flex items-center justify-between gap-3 rounded-xl bg-[#F8FAFC] px-5 py-4">
      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">{label}</p>
        <p data-testid={testid} className="mt-1 break-all font-heading text-sm sm:text-base font-bold text-[#0F2C59]">
          {value}
        </p>
      </div>
      <button
        type="button"
        onClick={copy}
        data-testid={`${testid}-copy`}
        aria-label={`Copier ${label}`}
        className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition-[color,border-color,transform] duration-200 hover:-translate-y-0.5 hover:border-[#0284C7] hover:text-[#0284C7]"
      >
        <Copy size={16} />
      </button>
    </div>
  );
};

const Don = () => (
  <div data-testid="don-page">
    <PageHero
      eyebrow="Faire un don"
      title="Votre générosité change leur quotidien"
      description="Chaque don, même modeste, aide une personne atteinte de la maladie de Waldmann à vivre mieux : soutien alimentaire, soutien financier au quotidien, accompagnement des familles."
      testid="don-title"
    />

    <section className="py-16 md:py-24">
      <div className="container-site">
        <div className="grid items-start gap-12 lg:grid-cols-[1fr_0.85fr]">
          <div>
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
                À quoi servent vos dons ?
              </h2>
              <div className="mt-8 space-y-5">
                {USES.map((u, i) => (
                  <div key={i} className="flex items-start gap-5 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm" data-testid={`donation-use-${i}`}>
                    <span className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#EFF6FF] text-[#0284C7]">
                      <u.icon size={22} />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-[#0F2C59]">{u.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-slate-600">{u.text}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-[#ECFDF5] p-6" data-testid="donation-tax-card">
                  <Receipt size={24} className="text-[#10B981]" />
                  <h3 className="mt-3 font-heading text-base font-bold text-[#065F46]">
                    66 % déductibles de vos impôts
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#065F46]/85">
                    Un don de 50 € ne vous coûte réellement que 17 € après déduction fiscale.
                    Un reçu vous est délivré pour chaque don.
                  </p>
                </div>
                <div className="rounded-2xl bg-[#EFF6FF] p-6" data-testid="donation-security-card">
                  <ShieldCheck size={24} className="text-[#0284C7]" />
                  <h3 className="mt-3 font-heading text-base font-bold text-[#0F2C59]">
                    Paiement 100 % sécurisé
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    Les dons en ligne transitent par PayPal, sans que vos données bancaires ne
                    soient jamais transmises à l'association.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div>
              <DonateWidget />
              <ReceiptForm />
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Virement bancaire */}
    <section className="pb-16 md:pb-24">
      <div className="container-site">
        <Reveal>
          <div
            data-testid="bank-transfer-section"
            className="card-site grid items-center gap-8 !p-8 sm:!p-12 lg:grid-cols-[0.9fr_1.1fr]"
          >
            <div>
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EFF6FF] text-[#0284C7]">
                <Landmark size={26} />
              </span>
              <h2 className="mt-5 font-heading text-xl sm:text-2xl font-semibold tracking-tight text-[#0F2C59]">
                Vous préférez donner par virement ?
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                C'est possible ! Effectuez un virement vers le compte de l'association avec les
                coordonnées ci-contre. Pensez à nous laisser vos coordonnées par e-mail pour
                recevoir votre reçu fiscal.
              </p>
              <p className="mt-4 rounded-xl bg-[#ECFDF5] px-4 py-3 text-sm font-semibold text-[#047857]">
                Le virement bancaire n'entraîne aucun frais pour l'association : 100 % de votre
                don nous parvient.
              </p>
            </div>
            <div className="space-y-3">
              <BankRow label="Titulaire du compte" value={BANK.titulaire} testid="bank-holder" />
              <BankRow label="Banque" value={BANK.banque} testid="bank-name" />
              <BankRow label="IBAN" value={BANK.iban} testid="bank-iban" />
              <BankRow label="BIC / SWIFT" value={BANK.bic} testid="bank-bic" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-[#EFF6FF] py-16 md:py-20">
      <div className="container-site text-center">
        <Reveal>
          <Heart size={36} className="mx-auto text-[#FF6B6B]" fill="currentColor" />
          <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[#0F2C59]">
            Merci, du fond du cœur.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
            Que vous donniez 10 € ou 100 €, une fois ou chaque mois, en ligne ou par virement :
            vous êtes à nos côtés, et aux côtés des malades. C'est ensemble que nous ferons
            reculer l'isolement face à la maladie de Waldmann.
          </p>
          <p className="mt-6 text-sm font-semibold text-slate-500">
            Une question sur les dons ? Écrivez-nous à{" "}
            <a href="mailto:aide.monde.et.sante@gmail.com" data-testid="don-email-link" className="text-[#0284C7] underline underline-offset-2">
              aide.monde.et.sante@gmail.com
            </a>{" "}
            ou appelez le{" "}
            <a href="tel:+33954243446" data-testid="don-phone-link" className="text-[#0284C7] underline underline-offset-2">
              09 54 24 34 46
            </a>
            .
          </p>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Don;
